/**
 * Primary / card image selection for products (supports optional backend fields).
 * PDP color → image: see getLinkedImageIndex (spec: trim, case-insensitive, hex-normalized).
 */

import { normalizeHexColor, normalizeHexFlexible } from './productColorDisplay';

export function getProductImageUrl(img) {
  if (img == null) return null;
  if (typeof img === 'string') return img;
  if (typeof img === 'object') return img.url || img.cloudinaryUrl || null;
  return null;
}

/**
 * URL for list cards, bag, orders: prefer flagged primary, then primaryImageIndex, then first.
 */
export function getPrimaryProductImageUrl(product) {
  if (!product) return null;
  const list = product.images;
  if (!Array.isArray(list) || list.length === 0) {
    return getProductImageUrl(product.image);
  }
  const primary = list.find(
    (img) =>
      img &&
      typeof img === 'object' &&
      (img.isPrimary === true || img.primary === true)
  );
  if (primary) return getProductImageUrl(primary);
  const idx = product.primaryImageIndex;
  if (typeof idx === 'number' && idx >= 0 && idx < list.length) {
    return getProductImageUrl(list[idx]);
  }
  return getProductImageUrl(list[0]);
}

/**
 * Index of the card/primary image (same rules as getPrimaryProductImageUrl).
 */
export function getPrimaryProductImageIndex(product) {
  if (!product) return 0;
  const list = product.images;
  if (!Array.isArray(list) || list.length === 0) return 0;
  const flagged = list.findIndex(
    (img) =>
      img &&
      typeof img === 'object' &&
      (img.isPrimary === true || img.primary === true)
  );
  if (flagged >= 0) return flagged;
  const idx = product.primaryImageIndex;
  if (typeof idx === 'number' && idx >= 0 && idx < list.length) return idx;
  return 0;
}

/** Per-image variant link from API (admin + public PDP). */
export function getLinkedColorFromImage(img) {
  if (img == null) return '';
  if (typeof img === 'object') {
    const v =
      img.linkedColor ??
      img.linked_color ??
      img.variantColor ??
      img.variant_color ??
      img.variant_color_name ??
      (img.meta && typeof img.meta === 'object'
        ? img.meta.linkedColor || img.meta.linked_color || img.meta.variantColor
        : undefined) ??
      img.color;
    return v != null && String(v).trim() !== '' ? String(v).trim() : '';
  }
  return '';
}

/**
 * Normalize color key for PDP matching (spec: trim; case-insensitive names; hex → lower #rrggbb).
 */
export function normalizeColorKeyForPdp(value) {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  const hex = normalizeHexFlexible(s) || normalizeHexColor(s);
  if (hex) return hex.toLowerCase();
  return s.toLowerCase();
}

/**
 * First image index whose linkedColor / variantColor matches selected color (spec reference).
 * Only object images with linkedColor fields participate; URL-only strings are skipped.
 */
export function getLinkedImageIndex(images, selectedColor) {
  const target = normalizeColorKeyForPdp(selectedColor);
  if (!target || !Array.isArray(images)) return -1;

  return images.findIndex((img) => {
    const raw = getLinkedColorFromImage(img);
    if (!raw) return false;
    const key = normalizeColorKeyForPdp(raw);
    return Boolean(key && key === target);
  });
}

/**
 * Variant keys shown on PDP: `colors[]` plus stock object keys (often the source of truth for hex vs name).
 */
export function getVariantColorKeyPalette(product) {
  if (!product) return [];
  const fromColors = Array.isArray(product.colors)
    ? product.colors.map((c) => (typeof c === 'string' ? c : String(c?.name || c?.value || c || '').trim())).filter(Boolean)
    : [];
  const stockKeys =
    product.stock && typeof product.stock === 'object'
      ? Object.keys(product.stock).filter((k) => typeof product.stock[k] === 'object' && product.stock[k] !== null)
      : [];
  const seen = new Set();
  const out = [];
  for (const c of [...fromColors, ...stockKeys]) {
    const n = normalizeColorKeyForPdp(c);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(String(c).trim());
  }
  return out.length ? out : fromColors;
}

/**
 * Index of the first product image whose linked variant color matches `colorKey`.
 * 1) Direct: normalized linkedColor === normalized selected (names / hex).
 * 2) Slot: same index in palette from `colors[]` + `stock` keys when direct fails (hex vs label mismatch).
 * 3) If no image has linkedColor (API omitted metadata), palette index i → image index i.
 */
export function getProductImageIndexForColor(product, colorKey) {
  if (!product || colorKey == null) return -1;
  if (String(colorKey).trim() === '') return -1;
  const list = product.images;
  if (!Array.isArray(list) || list.length === 0) return -1;

  const direct = getLinkedImageIndex(list, colorKey);
  if (direct >= 0) return direct;

  const palette = getVariantColorKeyPalette(product);
  if (palette.length === 0) return -1;

  const selectedNorm = normalizeColorKeyForPdp(colorKey);
  const selectedSlot = palette.findIndex((c) => normalizeColorKeyForPdp(c) === selectedNorm);
  if (selectedSlot < 0) return -1;

  const slotIdx = list.findIndex((img) => {
    const L = getLinkedColorFromImage(img);
    if (!L) return false;
    const linkedNorm = normalizeColorKeyForPdp(L);
    const linkedSlot = palette.findIndex((c) => normalizeColorKeyForPdp(c) === linkedNorm);
    return linkedSlot >= 0 && linkedSlot === selectedSlot;
  });
  if (slotIdx >= 0) return slotIdx;

  // API often omits linkedColor on images; slot logic above needs it. When no image has
  // linkedColor, map palette order to image index (colors[0] → images[0], colors[1] → images[1]).
  const hasAnyLinked = list.some((img) => getLinkedColorFromImage(img));
  if (!hasAnyLinked && selectedSlot < list.length) {
    return selectedSlot;
  }
  return -1;
}

/** Backend may send array-like objects `{ "0": {...}, "1": {...} }` instead of JSON arrays. */
function coerceArrayLikeToArray(value) {
  if (value == null) return null;
  if (Array.isArray(value)) return value;
  if (typeof value !== 'object') return null;
  const keys = Object.keys(value).filter((k) => /^\d+$/.test(k));
  if (keys.length === 0) return null;
  keys.sort((a, b) => Number(a) - Number(b));
  return keys.map((k) => value[k]);
}

/**
 * Merge URL-only `images[]` with parallel `imagesMeta` / snake_case aliases from API.
 * Also merges parallel string arrays: `linkedColors[]` / `linked_colors[]` (same length as `images`).
 */
export function normalizeProductImagesForStorefront(productData) {
  if (!productData) return [];
  let images = Array.isArray(productData.images) ? [...productData.images] : [];

  const parallelLinkedRaw =
    productData.linkedColors ||
    productData.linked_colors ||
    productData.imageLinkedColors ||
    productData.image_linked_colors;
  const parallelLinked = coerceArrayLikeToArray(parallelLinkedRaw) || parallelLinkedRaw;

  if (Array.isArray(parallelLinked) && parallelLinked.length > 0 && images.length > 0) {
    const n = Math.min(images.length, parallelLinked.length);
    for (let i = 0; i < n; i++) {
      const raw = parallelLinked[i];
      let lc = '';
      if (typeof raw === 'string') lc = raw.trim();
      else if (raw && typeof raw === 'object') {
        lc = String(
          raw.linkedColor || raw.linked_color || raw.variantColor || raw.color || ''
        ).trim();
      } else if (raw != null) lc = String(raw).trim();
      if (!lc) continue;
      const img = images[i];
      const existing = getLinkedColorFromImage(typeof img === 'object' ? img : { url: img });
      if (existing) continue;
      if (typeof img === 'string') {
        const u = img.trim();
        if (u) images[i] = { url: u, linkedColor: lc };
      } else if (img && typeof img === 'object') {
        images[i] = { ...img, linkedColor: lc };
      }
    }
  }

  const metaRaw =
    productData.imagesMeta ||
    productData.images_meta ||
    productData.imageMeta ||
    productData.image_meta ||
    productData.linkedColorsByImage ||
    productData.linked_colors_by_image;
  const meta = coerceArrayLikeToArray(metaRaw) || metaRaw;

  if (Array.isArray(meta) && meta.length > 0 && images.length > 0) {
    const n = Math.min(images.length, meta.length);
    for (let i = 0; i < n; i++) {
      const img = images[i];
      const m = meta[i];
      const hasObjectMeta =
        img &&
        typeof img === 'object' &&
        (img.linkedColor || img.linked_color || img.variantColor || img.variant_color);
      if (hasObjectMeta) continue;

      const lc =
        typeof m === 'string'
          ? m.trim()
          : m && typeof m === 'object'
            ? String(
                m.linkedColor ||
                  m.linked_color ||
                  m.variantColor ||
                  m.variant_color ||
                  ''
              ).trim()
            : '';
      const url =
        typeof img === 'string'
          ? img.trim()
          : img && typeof img === 'object'
            ? img.url || img.fullPath || img.src || img.path || img.cloudinaryUrl || ''
            : '';
      if (!url && !lc) continue;
      images[i] = {
        ...(typeof img === 'object' && img ? img : {}),
        url: url || (typeof img === 'string' ? img : ''),
        ...(lc ? { linkedColor: lc } : {}),
      };
    }
  }
  return images;
}
